/**
 * User profile photo (Bloco 21).
 *
 * This is the user's avatar in the interface — deliberately separate from a
 * Professional's signature image (domain/identity/professional.ts,
 * signatureImageId). The two are different records, stored apart, and neither
 * is ever copied into the other: a signature is documentary evidence, a photo
 * is interface identification, and reports never use the photo.
 */

export const ACCEPTED_PROFILE_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp"
] as const;

export const ACCEPTED_PROFILE_IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp"] as const;

/** 5 MB — enough for a photo, small enough to keep menus and lists responsive. */
export const MAX_PROFILE_IMAGE_BYTES = 5 * 1024 * 1024;

/** Approximate decoded size of a base64 data URL, without materialising the bytes. */
export function estimateDataUrlBytes(dataUrl: string | null | undefined): number {
  if (!dataUrl) return 0;
  const commaIndex = dataUrl.indexOf(",");
  const payload = commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
  const padding = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((payload.length * 3) / 4) - padding);
}

export function mimeTypeOfDataUrl(dataUrl: string | null | undefined): string {
  if (!dataUrl) return "";
  const match = /^data:([^;,]+)[;,]/.exec(dataUrl);
  return match ? match[1]!.toLowerCase() : "";
}

export interface ProfileImageCandidate {
  fileName?: string;
  mimeType?: string;
  sizeBytes?: number;
  dataUrl?: string;
}

/**
 * Field-specific validation messages — never a generic "arquivo inválido".
 * Checks the declared MIME type, the extension, the size and that the payload
 * really is an image data URL.
 */
export function validateProfileImage(candidate: ProfileImageCandidate): string[] {
  const errors: string[] = [];
  const dataUrl = candidate.dataUrl || "";

  if (!dataUrl.trim()) {
    errors.push("Selecione uma imagem.");
    return errors;
  }

  const declaredType = (candidate.mimeType || mimeTypeOfDataUrl(dataUrl)).toLowerCase();
  if (!(ACCEPTED_PROFILE_IMAGE_TYPES as readonly string[]).includes(declaredType)) {
    errors.push("Formato não aceito. Use PNG, JPG, JPEG ou WEBP.");
  }

  if (candidate.fileName) {
    const extension = candidate.fileName.split(".").pop()?.toLowerCase() || "";
    if (!(ACCEPTED_PROFILE_IMAGE_EXTENSIONS as readonly string[]).includes(extension)) {
      errors.push("Extensão do arquivo não aceita. Use .png, .jpg, .jpeg ou .webp.");
    }
  }

  if (!dataUrl.startsWith("data:image/")) {
    errors.push("O arquivo enviado não é uma imagem válida.");
  }

  const size = candidate.sizeBytes ?? estimateDataUrlBytes(dataUrl);
  if (size > MAX_PROFILE_IMAGE_BYTES) {
    const mb = (MAX_PROFILE_IMAGE_BYTES / (1024 * 1024)).toFixed(0);
    errors.push(`A imagem excede o tamanho máximo de ${mb} MB.`);
  }

  return errors;
}

/**
 * Fallback avatar for a user with no photo: "Tiago Carvalho" → "TC".
 * Never returns an empty string, so the interface never renders a broken
 * image, "null" or "undefined".
 */
export function userInitials(name: string | null | undefined, fallback = "?"): string {
  const cleaned = (name || "").trim().replace(/\s+/g, " ");
  if (!cleaned) return fallback;

  const parts = cleaned.split(" ").filter(Boolean);
  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : "";
  const initials = `${first}${last}`.toUpperCase();
  return initials || fallback;
}
